/**
 * What a plugin package does — its jobs, apps with live connection
 * availability, skills and deliverables. Account status comes from the
 * integration store; nothing about accounts lives in the manifest.
 */
import { useEffect } from "react";
import type {
  ConnectionStatus,
  IntegrationConnection,
} from "@/api/integrations";
import type {
  PluginApp,
  PluginAppAlternative,
  PluginDetail,
  PluginTask,
} from "@/api/plugins";
import {
  Button,
  CONCEPT_ICONS,
  getFrameTypeIcon,
  PocketRow,
  PocketSection,
} from "@/components/ui";
import { formatDate, translate, useT } from "@/i18n";
import { settingsPluginsMessages } from "@/i18n/messages/settings-plugins";
import { settingsSkillsMessages } from "@/i18n/messages/settings-skills";
import { COMPOSIO_UI_ENABLED, connectionStatusLabel } from "@/lib/composio";
import { useIntegrationStore } from "@/stores/integration-store";
import { SKILL_KIND_KEYS } from "./skill-library-state";
import { useSettingsNav } from "./settings-navigation";

/** An app's account status; inactive or absent reads as not connected. */
function connectionStatus(
  connection: IntegrationConnection | undefined,
  pending: boolean,
): ConnectionStatus | null {
  const status = pending ? "initiated" : connection?.status;
  return status && status !== "inactive" ? status : null;
}

const need = (required: boolean) =>
  translate(settingsPluginsMessages, required ? "required" : "optional");

/** How an app without an integration is read. */
const ALTERNATIVE_KEY = {
  computer_use: "appReadInBrowser",
  api_key: "appUsesApiKey",
} as const satisfies Record<PluginAppAlternative["kind"], string>;

/** The fallback for an app with an integration that is not connected. */
const FALLBACK_KEY = {
  computer_use: "appFallbackBrowser",
  api_key: "appFallbackApiKey",
} as const satisfies Record<PluginAppAlternative["kind"], string>;

/**
 * An app with an account or one Connect can work for; any other app is read
 * only through its alternative.
 */
function hasConnection(app: PluginApp, status: ConnectionStatus | null) {
  return Boolean(app.toolkit_slug) && (app.connectable || status !== null);
}

/** Need, then the account status, or how the app is read instead. */
function appLine(app: PluginApp, status: ConnectionStatus | null): string {
  const parts = [need(app.required)];
  const alternative = app.alternative?.kind;
  if (!hasConnection(app, status)) {
    if (alternative)
      parts.push(
        translate(settingsPluginsMessages, ALTERNATIVE_KEY[alternative]),
      );
  } else if (status) parts.push(connectionStatusLabel(status));
  else {
    parts.push(translate(settingsPluginsMessages, "notConnected"));
    if (alternative)
      parts.push(translate(settingsPluginsMessages, FALLBACK_KEY[alternative]));
  }
  return parts.join(" · ");
}

/**
 * Each app with its live account status, the readiness of a package. An
 * app without an integration, or one Connect cannot work for, says how it is
 * read and offers no Connect.
 */
function PluginApps({ apps }: { apps: PluginDetail["apps"] }) {
  const t = useT(settingsPluginsMessages);
  const nav = useSettingsNav();
  const connections = useIntegrationStore((state) => state.connections);
  const pending = useIntegrationStore((state) => state.pendingAuth);
  const actions = useIntegrationStore((state) => state.actions);
  useEffect(() => {
    if (COMPOSIO_UI_ENABLED) void actions.refreshConnections();
  }, [actions]);
  if (!apps.length) return null;
  return (
    <PocketSection title={t("apps")} testId="plugin-apps">
      {apps.map((app) => {
        const slug = app.toolkit_slug;
        const status = slug
          ? connectionStatus(connections.get(slug), pending.has(slug))
          : null;
        const openApp =
          slug && COMPOSIO_UI_ENABLED && hasConnection(app, status)
            ? () =>
                nav.push({
                  page: "connections-app",
                  args: { app: slug, title: app.name },
                })
            : undefined;
        return (
          <PocketRow
            key={app.key}
            testId={`plugin-app-${app.key}`}
            leading={<CONCEPT_ICONS.app className="h-4 w-4 text-muted" />}
            title={app.name}
            secondLine={appLine(app, status)}
            status={
              status === "expired" || status === "failed"
                ? { tone: status === "failed" ? "error" : "question" }
                : undefined
            }
            trailing={
              !status && openApp ? (
                <Button
                  size="sm"
                  variant="secondary"
                  data-testid={`plugin-app-connect-${app.key}`}
                  aria-label={t("connectApp", { name: app.name })}
                  onClick={openApp}
                >
                  {t("connect")}
                </Button>
              ) : undefined
            }
            chevron={Boolean(openApp)}
            onSelect={openApp}
          />
        );
      })}
    </PocketSection>
  );
}

type Schedule = NonNullable<PluginTask["schedule"]>;

const CADENCE_KEY = {
  daily: "cadenceDaily",
  weekdays: "cadenceWeekdays",
  weekly: "cadenceWeekly",
  biweekly: "cadenceBiweekly",
  monthly: "cadenceMonthly",
} as const;

const isCadence = (rrule: string): rrule is keyof typeof CADENCE_KEY =>
  Object.prototype.hasOwnProperty.call(CADENCE_KEY, rrule);

/** 1 January 2024 was a Monday; `rrule_day` 0 is Monday. */
const weekdayName = (day: number) =>
  formatDate(new Date(2024, 0, 1 + day), { weekday: "long" });

/** When the schedule runs, matching the package export's day wording. */
function scheduleDay(schedule: Schedule): string {
  if (schedule.rrule === "weekly" || schedule.rrule === "biweekly")
    return weekdayName(schedule.rrule_day ?? 0);
  if (schedule.rrule === "monthly")
    return translate(settingsPluginsMessages, "scheduleMonthDay", {
      day: schedule.rrule_day ?? "",
    });
  return "";
}

function scheduleSummary(schedule: Schedule): string {
  const cadence = isCadence(schedule.rrule)
    ? translate(settingsPluginsMessages, CADENCE_KEY[schedule.rrule])
    : schedule.rrule;
  const when = [scheduleDay(schedule), schedule.rrule_time, schedule.timezone];
  return `${cadence} · ${when.filter(Boolean).join(" ")}`;
}

/** When the job runs, then what it does in one line. */
function jobLine(task: PluginTask, boards: ReadonlyMap<string, string>) {
  const when =
    task.trigger === "pick"
      ? translate(settingsPluginsMessages, "triggerPick", {
          board: boards.get(task.board ?? "") ?? task.board ?? "",
        })
      : task.trigger === "start" || !task.schedule
        ? translate(settingsPluginsMessages, "triggerStart")
        : scheduleSummary(task.schedule);
  return [when, task.summary].filter(Boolean).join(" · ");
}

/** What the package does, one section per kind. */
export function PluginDetailSections({ detail }: { detail: PluginDetail }) {
  const t = useT(settingsPluginsMessages);
  const tSkills = useT(settingsSkillsMessages);
  const frames = new Map(
    detail.frames.map((frame) => [frame.key, frame.title]),
  );
  return (
    <>
      {detail.tasks.length > 0 && (
        <PocketSection title={t("jobs")} testId="plugin-jobs">
          {detail.tasks.map((task) => (
            <PocketRow
              key={task.key}
              testId={`plugin-job-${task.key}`}
              leading={<CONCEPT_ICONS.task className="h-4 w-4 text-muted" />}
              title={task.title}
              secondLine={jobLine(task, frames)}
            />
          ))}
        </PocketSection>
      )}
      <PluginApps apps={detail.apps} />
      {detail.skills.length > 0 && (
        <PocketSection title={t("skills")} testId="plugin-skills">
          {detail.skills.map((skill) => (
            <PocketRow
              key={`${skill.kind}:${skill.ref}`}
              testId={`plugin-skill-${skill.kind}-${skill.ref}`}
              leading={<CONCEPT_ICONS.skill className="h-4 w-4 text-muted" />}
              title={skill.ref}
              secondLine={`${tSkills(SKILL_KIND_KEYS[skill.kind])} · ${need(skill.required)} · ${skill.purpose}`}
            />
          ))}
        </PocketSection>
      )}
      {detail.frames.length > 0 && (
        <PocketSection title={t("deliverables")} testId="plugin-deliverables">
          {detail.frames.map((frame) => {
            const Icon = getFrameTypeIcon(frame.type);
            return (
              <PocketRow
                key={frame.key}
                testId={`plugin-frame-${frame.key}`}
                leading={<Icon className="h-4 w-4 text-muted" />}
                title={frame.title}
                secondLine={frame.type.toUpperCase()}
              />
            );
          })}
        </PocketSection>
      )}
    </>
  );
}
